/**
 * Voice-note playback: audio + ghost cursor on the slide + optional camera
 * follow, all driven by `audio.currentTime` against the note's track.
 *
 * One note plays at a time. The file is fetched with the Bearer header (an
 * `<audio src>` cannot carry it), decoded once, and the audio bytes become an
 * object URL. A single rAF loop positions the ghost element (a DOM node the
 * page attaches via `ghostRef`) and, when following, drives the viewer with
 * `setZoom` / `setCenter` inside a guard so the resulting `viewport-change`
 * is not mistaken for user input; any unguarded viewport change or drag turns
 * follow off.
 */
import { imageToScreen } from "@frontend/pages/case-workspace/pathology/lib/viewer-screen-coords"
import {
  decodeVoiceNoteFile,
  samplePointerAt,
  sampleViewportAt,
  type VoiceNoteFileV1
} from "@shared/extensions/voice-note/file"
import type { TileViewer } from "@virdx/wsi-viewer"
import { type RefObject, useCallback, useEffect, useRef, useState } from "react"
import { toast } from "sonner"

import type { VoiceNoteListItem } from "./use-voice-note-recorder"

const CURRENT_MS_UPDATE_INTERVAL = 100

interface UseVoiceNotePlaybackParams {
  viewerRef: RefObject<TileViewer | null>
  canvasRef: RefObject<HTMLCanvasElement | null>
  viewerEpoch: number
  activeSeriesId: string | null
  getAuthHeaders: () => Promise<Record<string, string> | undefined>
  /** Playback is disabled while a note is being recorded. */
  disabled: boolean
}

export interface VoiceNotePlaybackState {
  activeNote: VoiceNoteListItem | null
  isPlaying: boolean
  isLoading: boolean
  currentMs: number
  follow: boolean
  setFollow: (value: boolean) => void
  play: (note: VoiceNoteListItem) => Promise<void>
  pause: () => void
  resume: () => void
  stop: () => void
  seek: (ms: number) => void
  /** Attach to the ghost cursor element; the loop positions it directly. */
  ghostRef: RefObject<HTMLDivElement | null>
  /** Fetch + save a note's CBOR file. */
  download: (note: VoiceNoteListItem) => Promise<void>
}

async function fetchNoteBytes(
  fileUrl: string,
  getAuthHeaders: () => Promise<Record<string, string> | undefined>
): Promise<Uint8Array> {
  const headers = await getAuthHeaders()
  const res = await fetch(`/api${fileUrl}`, { headers })
  if (!res.ok) throw new Error(`Failed to load voice note (${res.status})`)
  return new Uint8Array(await res.arrayBuffer())
}

function safeFilename(title: string | null, id: string): string {
  const base = (title ?? "")
    .trim()
    .replace(/[^\w\- ]+/g, "")
    .replace(/\s+/g, "_")
  return `${base || `voice-note-${id.slice(0, 8)}`}.vxnote.cbor`
}

export function useVoiceNotePlayback({
  viewerRef,
  canvasRef,
  viewerEpoch,
  activeSeriesId,
  getAuthHeaders,
  disabled
}: UseVoiceNotePlaybackParams): VoiceNotePlaybackState {
  const [activeNote, setActiveNote] = useState<VoiceNoteListItem | null>(null)
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [currentMs, setCurrentMs] = useState(0)
  const [follow, setFollowState] = useState(false)

  const ghostRef = useRef<HTMLDivElement | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const objectUrlRef = useRef<string | null>(null)
  const fileRef = useRef<VoiceNoteFileV1 | null>(null)
  const activeNoteRef = useRef<VoiceNoteListItem | null>(null)
  const followRef = useRef(false)
  const applyingViewportRef = useRef(false)
  const loadSeqRef = useRef(0)
  const activeSeriesIdRef = useRef(activeSeriesId)

  useEffect(() => {
    activeSeriesIdRef.current = activeSeriesId
  }, [activeSeriesId])

  const setFollow = useCallback((value: boolean) => {
    followRef.current = value
    setFollowState(value)
  }, [])

  const releaseAudio = useCallback(() => {
    const audio = audioRef.current
    if (audio) {
      audio.pause()
      audio.removeAttribute("src")
      audio.load()
    }
    audioRef.current = null
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current)
      objectUrlRef.current = null
    }
    fileRef.current = null
  }, [])

  const stop = useCallback(() => {
    loadSeqRef.current += 1
    releaseAudio()
    activeNoteRef.current = null
    setActiveNote(null)
    setIsPlaying(false)
    setIsLoading(false)
    setCurrentMs(0)
    if (ghostRef.current) ghostRef.current.style.opacity = "0"
  }, [releaseAudio])

  const play = useCallback(
    async (note: VoiceNoteListItem) => {
      if (disabled) {
        toast.info("Stop recording before playing a note")
        return
      }
      if (!note.file_url) return
      // Same note: just resume.
      if (activeNoteRef.current?.id === note.id && audioRef.current) {
        void audioRef.current.play()
        return
      }
      stop()
      const seq = ++loadSeqRef.current
      activeNoteRef.current = note
      setActiveNote(note)
      setIsLoading(true)
      try {
        const bytes = await fetchNoteBytes(note.file_url, getAuthHeaders)
        if (seq !== loadSeqRef.current) return
        const file = decodeVoiceNoteFile(bytes)
        if (!file) throw new Error("The voice note file is corrupt")
        fileRef.current = file
        const blob = new Blob([file.audio as BlobPart], { type: file.mimeType })
        const url = URL.createObjectURL(blob)
        objectUrlRef.current = url
        const audio = new Audio(url)
        audio.preload = "auto"
        audio.onplay = () => setIsPlaying(true)
        audio.onpause = () => setIsPlaying(false)
        audio.onended = () => {
          setIsPlaying(false)
          setCurrentMs(file.durationMs)
        }
        audio.onerror = () => {
          toast.error("Could not play the voice note")
          stop()
        }
        audioRef.current = audio
        setIsLoading(false)
        await audio.play()
      } catch (error) {
        if (seq !== loadSeqRef.current) return
        toast.error("Could not load the voice note", {
          description: error instanceof Error ? error.message : undefined
        })
        stop()
      }
    },
    [disabled, getAuthHeaders, stop]
  )

  const pause = useCallback(() => {
    audioRef.current?.pause()
  }, [])

  const resume = useCallback(() => {
    void audioRef.current?.play()
  }, [])

  const seek = useCallback((ms: number) => {
    const audio = audioRef.current
    if (!audio) return
    audio.currentTime = Math.max(0, ms) / 1000
    setCurrentMs(Math.max(0, ms))
  }, [])

  const download = useCallback(
    async (note: VoiceNoteListItem) => {
      if (!note.file_url) return
      try {
        const bytes = await fetchNoteBytes(note.file_url, getAuthHeaders)
        const url = URL.createObjectURL(new Blob([bytes as BlobPart], { type: "application/cbor" }))
        const anchor = document.createElement("a")
        anchor.href = url
        anchor.download = safeFilename(note.title, note.id)
        document.body.appendChild(anchor)
        anchor.click()
        anchor.remove()
        setTimeout(() => URL.revokeObjectURL(url), 1000)
      } catch (error) {
        toast.error("Download failed", { description: error instanceof Error ? error.message : undefined })
      }
    },
    [getAuthHeaders]
  )

  // Recording started while a note plays → stop playback.
  useEffect(() => {
    if (disabled && activeNoteRef.current) stop()
  }, [disabled, stop])

  // rAF loop: ghost cursor + camera follow + slider position.
  useEffect(() => {
    if (!activeNote) return
    let raf = 0
    let lastStateUpdate = 0
    const tick = (now: number) => {
      const audio = audioRef.current
      const file = fileRef.current
      const ghost = ghostRef.current
      const viewer = viewerRef.current
      const canvas = canvasRef.current
      if (audio && file && viewer && canvas) {
        const tMs = audio.currentTime * 1000
        const onThisSlide = activeSeriesIdRef.current === file.seriesId
        if (ghost) {
          const pointer = onThisSlide ? samplePointerAt(file, tMs) : null
          const screen = pointer ? imageToScreen(viewer, canvas, pointer.x, pointer.y) : null
          if (screen?.visible) {
            ghost.style.opacity = "1"
            ghost.style.transform = `translate(${screen.x}px, ${screen.y}px)`
          } else {
            ghost.style.opacity = "0"
          }
        }
        if (followRef.current && onThisSlide && !audio.paused) {
          const target = sampleViewportAt(file, tMs)
          if (target) {
            applyingViewportRef.current = true
            try {
              viewer.setZoom(target.zoom)
              viewer.setCenter(target.cx, target.cy)
            } finally {
              applyingViewportRef.current = false
            }
          }
        }
        if (now - lastStateUpdate > CURRENT_MS_UPDATE_INTERVAL) {
          lastStateUpdate = now
          setCurrentMs(tMs)
        }
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [activeNote, canvasRef, viewerRef])

  // User took the camera: any viewport change we did not cause turns follow off.
  useEffect(() => {
    const viewer = viewerRef.current
    if (!viewer || viewerEpoch === 0) return
    const onUserViewport = () => {
      if (applyingViewportRef.current || !followRef.current) return
      setFollow(false)
    }
    viewer.on("viewport-change", onUserViewport)
    viewer.on("drag-start", onUserViewport)
    return () => {
      viewer.off("viewport-change", onUserViewport)
      viewer.off("drag-start", onUserViewport)
    }
  }, [viewerRef, viewerEpoch, setFollow])

  // Release audio + object URL on unmount.
  useEffect(() => () => releaseAudio(), [releaseAudio])

  return {
    activeNote,
    isPlaying,
    isLoading,
    currentMs,
    follow,
    setFollow,
    play,
    pause,
    resume,
    stop,
    seek,
    ghostRef,
    download
  }
}
